import * as msal from "@azure/msal-browser";
import {
  AuthenticationResult,
  BrowserCacheLocation,
  EventType,
} from "@azure/msal-browser";
import { useCallback, useRef } from "react";
const ua = window.navigator.userAgent;
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const firefox = ua.indexOf("Firefox");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0; // Only needed if you need to support the redirect flow in Firefox incognito

export const b2cPolicies = {
  names: {
    signIn: `${import.meta.env.VITE_REACT_APP_B2C_SI_POLICY}`,
    imp: `${import.meta.env.VITE_REACT_APP_B2C_IMP_POLICY}`,
  },
  authorities: {
    signIn: {
      authority: `${import.meta.env.VITE_REACT_APP_AUTH_B2C_PATH}${
        import.meta.env.VITE_REACT_APP_B2C_SI_POLICY
      }`,
    },
    imp: {
      authority: `${import.meta.env.VITE_REACT_APP_AUTH_B2C_PATH}${
        import.meta.env.VITE_REACT_APP_B2C_IMP_POLICY
      }`,
    },
  },
  authorityDomain: `${import.meta.env.VITE_REACT_APP_B2C_AUTHORITY}`,
};
const msalConfig: msal.Configuration = {
  auth: {
    clientId: `${import.meta.env.VITE_REACT_APP_CLIENT_ID}`,
    // clientId,
    authority: b2cPolicies.authorities.signIn.authority,
    // authoriryB2cPath,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: `/inicio`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage,
    storeAuthStateInCookie: isIE || isEdge || isFirefox,
  },
};

export const msalImpConfig: msal.Configuration = {
  auth: {
    clientId: `${import.meta.env.VITE_REACT_APP_CLIENT_ID}`,
    // clientId,
    authority: b2cPolicies.authorities.imp.authority,
    // authoriryB2cPath,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: `/internal-app`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage,
    storeAuthStateInCookie: true,
  },
};

export const MSAL_IMP_PARAMS = {
  APP: "app",
  TARGET: "target",
  ID: "id",
  TS: "ts",
};
export const MSAL_STORAGE_KEYS = {
  CONFIG: "msalConfig",
  INSTANCE: "msalInstance",
  ERROR: "msalError",
};

export const MSAL_IMP_APPS = {
  HISAM: "HISAM",
  SIP: "SIP",
};

// Shared by this document's provider and hooks, not by other iframes or WebViews.
let msalInstanceSingleton: msal.PublicClientApplication | null = null;
let initializationPromise: Promise<void> | null = null;
let redirectHash = "";

function useMsalB2C() {
  if (!msalInstanceSingleton) {
    // Choose the base configuration once; each login request still sets its own policy.
    const isInternal = window.location.pathname.toLowerCase() === "/internal-app";
    const instance = new msal.PublicClientApplication(isInternal ? msalImpConfig : msalConfig);
    msalInstanceSingleton = instance;
    // Preserve callback detection even if MsalProvider removes the hash.
    redirectHash = isInternal ? window.location.hash : "";
    initializationPromise = instance.initialize();
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        instance.setActiveAccount(
          (event.payload as AuthenticationResult).account
        );
      }
    });
  }

  const msalInstance = msalInstanceSingleton;
  // Repeated effects share a request within this hook mount, including StrictMode.
  const normalLoginPromise = useRef<Promise<AuthenticationResult | null> | null>(null);
  const internalLoginPromise = useRef<{
    search: string;
    promise: Promise<AuthenticationResult | null>;
  } | null>(null);

  const loginRequest = useCallback(() => {
    if (!normalLoginPromise.current) {
      normalLoginPromise.current = (async () => {
        await initializationPromise;
        let result: AuthenticationResult | null = null;
        try {
          result = await msalInstance.handleRedirectPromise();
          if (result) msalInstance.setActiveAccount(result.account);
        } catch (error: unknown) {
          console.error("MSAL redirect error:", error);
          // Normal login recovers only when no active or cached session remains.
          if (msalInstance.getActiveAccount() || msalInstance.getAllAccounts().length > 0) {
            return null;
          }
        }
        if (!msalInstance.getActiveAccount()) {
          localStorage.clear();
          await msalInstance.loginRedirect({
            authority: b2cPolicies.authorities.signIn.authority,
            redirectUri: msalConfig.auth.redirectUri,
            scopes: ["openid", "profile", "offline_access"],
          });
        }
        return result;
      })();
    }
    return normalLoginPromise.current;
  }, [msalInstance]);

  const internalLoginRequest = useCallback((search: string) => {
    if (!internalLoginPromise.current || internalLoginPromise.current.search !== search) {
      const promise = (async () => {
        await initializationPromise;
        const responseParams = new URLSearchParams(redirectHash.replace(/^#/, ""));
        const isCallback = responseParams.has("code") ||
          responseParams.has("error") || responseParams.has("state");
        // Use the same promise cache key as MsalProvider to avoid redeeming twice.
        const result = await msalInstance.handleRedirectPromise();
        if (isCallback) {
          // A callback must finish here: never restart impersonation after an error.
          if (!result?.idToken) {
            throw new Error("La autenticacion no ha devuelto un token valido.");
          }
          msalInstance.setActiveAccount(result.account);
          redirectHash = "";
          return result;
        }

        // A new entry impersonates the requested patient even if another account exists.
        const params = new URLSearchParams(search);
        const app = params.get(MSAL_IMP_PARAMS.APP);
        const target = params.get(MSAL_IMP_PARAMS.TARGET);
        const id = params.get(MSAL_IMP_PARAMS.ID);
        const ts = params.get(MSAL_IMP_PARAMS.TS);
        if (!app?.trim() || !target?.trim() || !id?.trim() || !ts?.trim()) {
          throw new Error("Faltan parametros de impersonacion: app, target, id y ts.");
        }
        await msalInstance.loginRedirect({
          authority: b2cPolicies.authorities.imp.authority,
          redirectUri: msalImpConfig.auth.redirectUri,
          scopes: ["openid", "profile", "offline_access"],
          extraQueryParameters: {
            app,
            targetUsername: target,
            impersonatorId: id,
            ts: encodeURIComponent(ts),
            ui_locales: "es",
          },
        });
        return null;
      })();
      internalLoginPromise.current = { search, promise };
    }
    return internalLoginPromise.current.promise;
  }, [msalInstance]);

  return { msalInstance, loginRequest, internalLoginRequest };
}

export default useMsalB2C;
